import { useQueryClient } from "@tanstack/react-query";
import { message } from "antd";
import { useApiMutation, useApiQuery } from "../hooks/useApiRequest";

export interface UserRecord {
  _id?: string;
  email?: string;
  username?: string;
  payID?: string;
  status?: string;
  personal_info?: {
    fullName?: string;
    firstName?: string;
    lastName?: string;
    name?: string;
    legal_name?: string;
    lastname?: string;
    legal_lastname?: string;
  };
}

export interface UserListResponse {
  results?: UserRecord[];
}

export interface PaginatedUserListResponse {
  results?: UserRecord[];
  totalItems?: number;
  totalPages?: number;
  currentPage?: number;
  limit?: number;
}

interface UserListQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  administrativeRoleId?: string;
  scope?: string;
}

export interface SecretResponse {
  otpauth_url?: string;
  base32?: string;
}

export interface UserBalanceRecord {
  _id?: string;
  asset?: string;
  availableAmount?: string;
  availableAmountFormatted?: string;
  available?: {
    $numberDecimal?: string;
  };
  accountName?: string;
  accountId?: string;
}

export interface LoginHistoryPayload {
  ipAddress: string;
  device: string;
  browser: string;
}

interface QueryValues {
  [key: string]: unknown;
}

interface ErrorWithMessage {
  message?: string;
}

const getErrorMessage = (error: unknown, fallback: string) => {
  if (error instanceof Error) {
    return error.message || fallback;
  }

  if (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof (error as ErrorWithMessage).message === "string"
  ) {
    return (error as ErrorWithMessage).message || fallback;
  }

  return fallback;
};

//* ---------Get all users---------
export const useGetAllUsers = () => {
  return useApiQuery<UserListResponse>({
    key: ["clients"],
    endPoint: `/api/v2/admin/users`,
    method: "GET",
  });
};

export const useGetAdminUsersPageService = ({
  page = 1,
  limit = 10,
  search,
  administrativeRoleId,
  scope,
}: UserListQueryParams = {}) => {
  const normalizedSearch = search?.trim();
  const normalizedAdministrativeRoleId = administrativeRoleId?.trim();
  const normalizedScope = scope?.trim();
  const searchSuffix = normalizedSearch
    ? `&search=${encodeURIComponent(normalizedSearch)}`
    : "";
  const administrativeRoleSuffix = normalizedAdministrativeRoleId
    ? `&administrativeRoleId=${encodeURIComponent(normalizedAdministrativeRoleId)}`
    : "";
  const scopeSuffix = normalizedScope
    ? `&scope=${encodeURIComponent(normalizedScope)}`
    : "";

  return useApiQuery<PaginatedUserListResponse>({
    key: [
      "clients-index",
      page,
      limit,
      normalizedSearch,
      normalizedAdministrativeRoleId,
      normalizedScope,
    ],
    endPoint: `/api/v2/admin/users?page=${page}&limit=${limit}${searchSuffix}${administrativeRoleSuffix}${scopeSuffix}`,
    method: "GET",
  });
};

export const useGetUserAccounts = (userId?: string) => {
  return useApiQuery({
    key: ["chains"],
    endPoint: `/api/v2/sub-accounts?userId=${userId}`,
    method: "GET",
  });
};

//* ---------Get user balances---------
export const useGetUserBalances = (userId?: string) => {
  const endpoint = userId
    ? `/api/v2/admin/clients/${userId}/balances`
    : `/api/v2/admin/clients`;

  return useApiQuery<UserBalanceRecord[]>({
    key: ["user-balances", userId],
    endPoint: endpoint,
    method: "GET",
    queryOptions: {
      enabled: !!userId,
    },
  });
};

//*********** add Login History **********
export const useAddLoginHistoryService = () => {
  const queryClient = useQueryClient();
  const { mutateAsync, error } = useApiMutation();

  const addLoginHistory = async (
    values: LoginHistoryPayload,
    onSuccess: () => void,
    onError: () => void
  ) => {
    try {
      await mutateAsync({
        key: ["login-history"],
        endPoint: `/api/v2/login-history`,
        method: "POST",
        bodyContent: values,
      });
      queryClient.invalidateQueries({ queryKey: ["login-history"] });
      onSuccess();
    } catch (error) {
      onError();
      message.error(getErrorMessage(error, "Failed to add login history."));
    }
  };

  return { addLoginHistory, error };
};

//* ---------Get Secret Key---------
export const useGetSecret = () => {
  return useApiQuery<SecretResponse>({
    key: ["secret-key"],
    endPoint: `/api/v2/settings/security/2fa/secret`,
    method: "GET",
  });
};

//*********** Verify Code **********
export const useVerifyCodeService = () => {
  const queryClient = useQueryClient();
  const { mutateAsync, error } = useApiMutation();

  const verifyCode = async (
    values: QueryValues,
    onSuccess: () => void,
    onError: () => void
  ) => {
    try {
      await mutateAsync({
        key: ["verify-codes"],
        endPoint: `/api/v2/settings/security/2fa/verify`,
        method: "PATCH",
        bodyContent: {
          code:
            typeof values.code === "string"
              ? values.code
              : typeof values.token === "string"
                ? values.token
                : "",
        },
      });
      queryClient.invalidateQueries({ queryKey: ["secret-key"] });
      queryClient.invalidateQueries({ queryKey: ["verify-codes"] });
      onSuccess();
      message.success("2FA successfully enabled");
    } catch (error) {
      onError();
      message.error(getErrorMessage(error, "Failed to verify code."));
    }
  };

  return { verifyCode, error };
};
